import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Users, User, LogOut, Bell, Sparkles, Megaphone,
    ShieldCheck, Sun, Moon, Menu, ChevronRight, Edit3, Save, X, LayoutDashboard
} from "lucide-react";
import API_URL from '../../config';
import AnnouncementsBanner from "../../components/AnnouncementsBanner";
import "../Home.css"; // Reuse student home CSS
import "./FacultyHome.css";

const FacultyHome = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState({ name: "", email: "", role: "" });
    const [facultyProfile, setFacultyProfile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [theme, setTheme] = useState(() => localStorage.getItem("vjconnect_theme") || "dark");
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [alerts, setAlerts] = useState({ total: 0 });
    const menuRef = useRef(null);
    const notifRef = useRef(null);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
        if (!userData.email || (userData.role !== "faculty" && userData.role !== "admin")) {
            navigate("/");
        } else {
            setUser(userData);
            fetchFacultyProfile(userData.email);
            fetchAlerts(userData.email);
        }
    }, [navigate]);

    // Log active session visit
    useEffect(() => {
        if (!user.email) return;
        fetch(`${API_URL}/auth/session-ping`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: user.email, name: user.name, role: user.role || "faculty" })
        }).catch(e => console.warn("Failed to ping session", e));
    }, [user.email, user.name, user.role]);

    const fetchAlerts = async (email) => {
        try {
            const res = await fetch(`${API_URL}/attendance/faculty-alerts/${email}`);
            const data = await res.json();
            setAlerts(data);
        } catch (err) { console.error('Failed to fetch alerts', err); }
    };

    const fetchFacultyProfile = async (email) => {
        try {
            const res = await fetch(`${API_URL}/faculty/profile?email=${email}`);
            const data = await res.json();
            if (data.exists) {
                setFacultyProfile(data.profile);
                setEditData(data.profile);
            }
        } catch (err) {
            console.error("Failed to fetch faculty profile", err);
        }
    };

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("vjconnect_theme", theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === "dark" ? "light" : "dark");
    };

    const handleLogout = () => {
        localStorage.removeItem("vjconnect_user");
        navigate("/");
    };

    const handleSaveProfile = async () => {
        try {
            const res = await fetch(`${API_URL}/faculty/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: user.email, ...editData })
            });
            if (res.ok) {
                setFacultyProfile(editData);
                setIsEditing(false);
                alert("Profile updated successfully!");
            }
        } catch (err) {
            console.error("Update error", err);
            alert("Failed to update profile");
        }
    };

    // Derived information
    const getJoinYear = (empId) => {
        if (!empId) return "N/A";
        const yearPrefix = empId.substring(0, 2);
        return `20${yearPrefix}`;
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notifRef.current && !notifRef.current.contains(event.target)) setNotifOpen(false);
            if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 18) return "Good Afternoon";
        return "Good Evening";
    };

    const facultyFeatures = [
        {
            name: "Complete Profile",
            link: "/faculty/profile",
            icon: <User size={32} />,
            desc: "Manage your detailed rich profile, certifications, and research.",
            color: "from-pink-500 to-rose-600"
        },
        {
            name: "Team Formation",
            link: "/faculty/admin",
            icon: <Users size={32} />,
            desc: "Manage and create teams for various events.",
            color: "from-violet-600 to-purple-700"
        },
        {
            name: "Explore",
            link: "/explore",
            icon: <Sparkles size={32} />,
            desc: "Discover & connect with people across departments.",
            color: "from-emerald-500 to-teal-600"
        },
        {
            name: "Announce",
            link: "/faculty/announcements",
            icon: <Megaphone size={32} />,
            desc: "Broadcast messages to departments or sections.",
            color: "from-amber-500 to-orange-600"
        },
        {
            name: "Attendance",
            link: "/faculty/attendance",
            icon: <ShieldCheck size={32} />,
            desc: "Approve & record student attendance requests.",
            color: "from-blue-500 to-indigo-600"
        },
        {
            name: "Timetable Manager",
            link: "/faculty/timetable",
            icon: <Sun size={32} />,
            desc: "Fill and manage your personal class schedule.",
            color: "from-blue-500 to-indigo-600"
        }
    ];

    if (alerts.total > 0) {
        const attFeature = facultyFeatures.find(f => f.name === "Attendance");
        if (attFeature) {
            attFeature.badge = `${alerts.total} New`;
        }
    }

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper">
                <header className="home-header">
                    <div className="brand-section">
                        <h1 className="brand-title">Faculty Hub</h1>
                        <span className="greeting-text">{getGreeting()}, Prof. {facultyProfile?.name || user.name}</span>
                    </div>

                    <div className="header-actions">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="icon-btn"
                            onClick={toggleTheme}
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </motion.button>
                        <div style={{ position: 'relative' }} ref={notifRef}>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="icon-btn"
                                onClick={() => setNotifOpen(!notifOpen)}
                            >
                                <Bell size={20} />
                            </motion.button>
                        </div>
                        <div style={{ position: 'relative' }} ref={menuRef}>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="icon-btn"
                                onClick={() => setMenuOpen(!menuOpen)}
                            >
                                <Menu size={20} />
                            </motion.button>
                            <AnimatePresence>
                                {menuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95, x: 10 }}
                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, x: 10 }}
                                        className="notifications-dropdown"
                                        style={{ width: '200px' }}
                                    >
                                        <button className="search-result-row" onClick={() => navigate('/faculty/profile')}>
                                            <User size={18} /> My Profile
                                        </button>
                                        <div style={{ height: '1px', background: 'var(--glass-border)', margin: '0.5rem 0' }} />
                                        <button className="search-result-row" onClick={handleLogout}>
                                            <LogOut size={18} /> Logout
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </header>

                <main style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

                    <AnnouncementsBanner user={{...user, ...(facultyProfile || {})}} />

                    {/* Faculty Profile Card */}
                    <section className="glass-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div className="tile-icon-box" style={{ width: '60px', height: '60px', padding: '0' }}>
                                    {facultyProfile?.profile_pic ? (
                                        <img src={facultyProfile.profile_pic} alt="profile" style={{ width: '100%', height: '100%', borderRadius: '16px', objectFit: 'cover' }} />
                                    ) : (
                                        <User size={30} />
                                    )}
                                </div>
                                <div>
                                    <h2 style={{ fontSize: '1.25rem', margin: 0 }}>Faculty Profile</h2>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: 0 }}>{user.email}</p>
                                </div>
                            </div>
                            <button
                                className="icon-btn"
                                onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                                style={{ background: isEditing ? 'var(--accent-color)' : 'transparent' }}
                            >
                                {isEditing ? <Save size={20} /> : <Edit3 size={20} />}
                            </button>
                        </div>

                        <div style={{ height: '1px', background: 'var(--glass-border)' }} />

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                            <div className="profile-field">
                                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Name</label>
                                {isEditing ? (
                                    <input
                                        className="search-input-modern"
                                        value={editData.name || ""}
                                        onChange={e => setEditData({ ...editData, name: e.target.value })}
                                        style={{ padding: '0.5rem', width: '100%', marginTop: '0.3rem' }}
                                    />
                                ) : (
                                    <div style={{ fontWeight: 600 }}>{facultyProfile?.name || user.name}</div>
                                )}
                            </div>

                            <div className="profile-field">
                                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Employee ID</label>
                                {isEditing ? (
                                    <input
                                        className="search-input-modern"
                                        value={editData.emp_id || ""}
                                        onChange={e => setEditData({ ...editData, emp_id: e.target.value })}
                                        placeholder="e.25CS01"
                                        style={{ padding: '0.5rem', width: '100%', marginTop: '0.3rem' }}
                                    />
                                ) : (
                                    <div style={{ fontWeight: 600 }}>{facultyProfile?.emp_id || "Not Set"}</div>
                                )}
                            </div>

                            <div className="profile-field">
                                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Department</label>
                                {isEditing ? (
                                    <input
                                        className="search-input-modern"
                                        value={editData.work_dept || ""}
                                        onChange={e => setEditData({ ...editData, work_dept: e.target.value })}
                                        style={{ padding: '0.5rem', width: '100%', marginTop: '0.3rem' }}
                                    />
                                ) : (
                                    <div style={{ fontWeight: 600 }}>{facultyProfile?.work_dept || "Not Set"}</div>
                                )}
                            </div>

                            <div className="profile-field">
                                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Room Number</label>
                                {isEditing ? (
                                    <input
                                        className="search-input-modern"
                                        value={editData.room_no || ""}
                                        onChange={e => setEditData({ ...editData, room_no: e.target.value })}
                                        style={{ padding: '0.5rem', width: '100%', marginTop: '0.3rem' }}
                                    />
                                ) : (
                                    <div style={{ fontWeight: 600 }}>{facultyProfile?.room_no || "Not Set"}</div>
                                )}
                            </div>

                            {!isEditing && (
                                <div className="profile-field">
                                    <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Year of Joining</label>
                                    <div style={{ fontWeight: 600, color: 'var(--accent-color)' }}>{getJoinYear(facultyProfile?.emp_id)}</div>
                                </div>
                            )}
                        </div>
                        {isEditing && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                                <button onClick={() => setIsEditing(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>Cancel</button>
                                <button
                                    onClick={handleSaveProfile}
                                    style={{ background: 'var(--accent-color)', color: 'white', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '8px', cursor: 'pointer' }}
                                >
                                    Save Changes
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Faculty Bento Grid */}
                    <section className="features-grid-bento">
                        {facultyFeatures.map((feature, idx) => (
                            <motion.div
                                key={feature.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                            >
                                <Link to={feature.link} className="feature-tile">
                                    <div className="tile-icon-box">
                                        {feature.icon}
                                    </div>
                                    <div className="tile-content">
                                        <div className="tile-title">
                                            {feature.name}
                                            {feature.badge && <span className="faculty-special-badge">{feature.badge}</span>}
                                        </div>
                                        <div className="tile-desc">{feature.desc}</div>
                                    </div>
                                    <div className="feature-tile-bg-circle" />
                                </Link>
                            </motion.div>
                        ))}
                    </section>

                    <section className="glass-card" style={{ padding: '2rem', textAlign: 'center' }}>
                        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Welcome to the VJ Link Faculty Hub</h2>
                        <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                            This dashboard provides specialized tools for faculty and administrators to streamline academic processes, manage teams, and connect with the student community.
                        </p>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default FacultyHome;
